// The friendly rule syntax and its two-way translation to the stored predicate.
//
// Normal users write name-based tokens — the predicate with entity UUIDs is generated
// behind the scenes and is what gets stored and evaluated by the server:
//
//   #TAG:BEBIDA AND (#BA:PIANO BAR OR #DESK:CASH DESK PIANO BAR)
//     →  tags CONTAINS 'BEBIDA' && (businessAreaID == '…' || cashDeskID == '…')
//
// Tokens: #BA:<business area>  #DESK:<cashdesk>  #PM:<pay method>  #TAG:<tag>
//         #CARD:<subtype>, combined with AND / OR and parentheses. Empty = catch-all.
// A token value runs until AND / OR, a parenthesis or the end — names keep their spaces.

interface RuleTokenContext {
    byId: {[uuid:string]: {kind:string, name:string}};          // uuid (uppercased) → token kind + entity name
    byKindName: {[kind:string]: {[nameUpper:string]: string}};  // kind → NAME (uppercased) → uuid
    names: {[kind:string]: string[]};                           // kind → canonical names (for suggestions)
    tags: string[];
    subtypes: string[];
}

class OperaRuleExpression {

    static kindField(kind: string): string {
        switch (kind) {
            case "BA":   return "businessAreaID";
            case "DESK": return "cashDeskID";
            case "PM":      return "payMethodID";
            case "MENU":    return "menuID";
            case "PRODUCT": return "productID";
        }
        return null;
    }

    static fieldKind(field: string): string {
        switch (field) {
            case "businessAreaID": return "BA";
            case "cashDeskID":     return "DESK";
            case "payMethodID":    return "PM";
            case "menuID":         return "MENU";
            case "productID":      return "PRODUCT";
        }
        return null;
    }

    static kindLabel(kind: string): string {
        switch (kind) {
            case "BA":   return MIOLocalizeString("BUSINESS_AREA", "business area");
            case "DESK": return MIOLocalizeString("CASHDESK", "cashdesk");
            case "PM":   return MIOLocalizeString("PAY_METHOD", "pay method");
            case "TAG":  return MIOLocalizeString("TAG", "tag");
            case "CARD": return MIOLocalizeString("CARD_SUBTYPE", "card subtype");
            case "MENU": return MIOLocalizeString("MENU", "menu");
            case "PRODUCT": return MIOLocalizeString("PRODUCT", "product");
        }
        return kind;
    }

    // friendly → predicate. Returns predicate null + error text when a token cannot be
    // resolved — the caller keeps the stored rule untouched until the text is valid.
    static compile(friendly: string, ctx: RuleTokenContext): {predicate: string, error: string} {
        let text = (friendly || "").trim();
        if (text.length == 0) return {predicate: null, error: null};    // catch-all

        let error: string = null;
        let s = text.replace(/#(BA|DESK|PM|TAG|CARD|MENU|PRODUCT):([^#()]*?)(?=\s+(?:AND|OR)\b|\s*[()]|\s*$)/gi, (m, kind, value) => {
            kind = kind.toUpperCase();
            value = value.trim();
            if (value.length == 0) { if (error == null) error = "#" + kind + ": " + MIOLocalizeString("NEEDS_A_NAME", "needs a name"); return m; }
            if (kind == "TAG")  return "tags CONTAINS '" + value + "'";
            if (kind == "CARD") return "payMethodSubtype == '" + value.toLowerCase() + "'";

            let id = (ctx.byKindName[kind] || {})[value.toUpperCase()];
            if (id == null) {
                if (error == null) error = OperaRuleExpression.kindLabel(kind) + " “" + value + "” " + MIOLocalizeString("NOT_FOUND", "not found");
                return m;
            }
            return OperaRuleExpression.kindField(kind) + " == '" + id + "'";
        });
        if (error != null) return {predicate: null, error: error};

        // Bare #MENU = "is a menu", no specific one.
        s = s.replace(/#MENU(?=\s|$|[()])/gi, "menu == true");
        s = s.replace(/\bAND\b/gi, "&&").replace(/\bOR\b/gi, "||");
        if (s.indexOf("#") >= 0) return {predicate: null, error: MIOLocalizeString("UNRECOGNIZED_TOKEN", "unrecognized token") + ": " + s.substring(s.indexOf("#")).split(" ")[0]};

        return {predicate: s.replace(/\s+/g, " ").trim(), error: null};
    }

    // predicate → friendly. exact means the translation round-trips — rows that don't
    // (hand-written predicates beyond the token grammar) stay in raw mode.
    static decompile(predicate: string, ctx: RuleTokenContext): {friendly: string, exact: boolean} {
        let text = (predicate || "").trim();
        if (text.length == 0) return {friendly: "", exact: true};

        let s = text;
        s = s.replace(/menu == true/g, "#MENU");
        s = s.replace(/(payMethodID|businessAreaID|cashDeskID|menuID|productID) == '([0-9A-Fa-f-]{36})'/g, (m, field, uuid) => {
            let e = ctx.byId[uuid.toUpperCase()];
            if (e == null) return m;
            return "#" + OperaRuleExpression.fieldKind(field) + ":" + e.name;
        });
        s = s.replace(/tags CONTAINS '([^']+)'/g, "#TAG:$1");
        s = s.replace(/payMethodSubtype == '([^']+)'/g, "#CARD:$1");
        s = s.replace(/&&/g, "AND").replace(/\|\|/g, "OR");
        s = s.replace(/\s+/g, " ").trim();

        // Round-trip check is the exactness test — no grammar guesswork. Whitespace and
        // the case of ids are not grammar: a hand-typed lowercase uuid is the same rule.
        let back = OperaRuleExpression.compile(s, ctx);
        let exact = back.error == null && back.predicate != null
                 && OperaRuleExpression.normalized(back.predicate) == OperaRuleExpression.normalized(text);
        return {friendly: s, exact: exact};
    }

    static normalized(predicate: string): string {
        return (predicate || "").replace(/\s+/g, " ").trim()
            .replace(/'([0-9A-Fa-f-]{36})'/g, (m, uuid) => "'" + uuid.toUpperCase() + "'");
    }

    // Suggestions for the token being typed at `caret` in `text`.
    // Right after '#' → the token kinds; after '#KIND:' → names filtered by the prefix.
    static suggestions(text: string, caret: number, ctx: RuleTokenContext): {replaceFrom: number, items: {insert: string, display: string, hint: string}[]} {
        let before = text.substring(0, caret);
        let m = before.match(/#(\w*)(?::([^#()]*))?$/);
        if (m == null) return null;

        let start = before.length - m[0].length;
        let kind = (m[1] || "").toUpperCase();
        let hasColon = m[0].indexOf(":") >= 0;

        if (!hasColon) {
            let kinds = ["BA", "DESK", "PM", "TAG", "CARD"].filter(k => k.indexOf(kind) == 0 || kind.length == 0);
            return {replaceFrom: start, items: kinds.map(k => ({insert: "#" + k + ":", display: "#" + k + ":", hint: OperaRuleExpression.kindLabel(k)}))};
        }

        let typed = (m[2] || "").trim().toUpperCase();
        let pool: string[] = [];
        if (kind == "TAG") pool = ctx.tags;
        else if (kind == "CARD") pool = ctx.subtypes;
        else pool = ctx.names[kind] || [];

        let items = pool.filter(n => typed.length == 0 || n.toUpperCase().indexOf(typed) >= 0)
                        .slice(0, 8)
                        .map(n => ({insert: "#" + kind + ":" + n, display: n, hint: OperaRuleExpression.kindLabel(kind)}));
        if (items.length == 0) return null;
        return {replaceFrom: start, items: items};
    }
}
